import React, { useMemo, useState } from 'react';
import {
  Box, Checkbox, Chip, Drawer, IconButton, Skeleton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { AppButton } from '../../../components/UI';
import { EmptyState, ListToolbar, Pager, SectionTitle, Tags } from './ui';
import ProfileSummary, { SeniorityChip } from './ProfileSummary';
import { candidateLocation, candidateName, candidateRole, candidateUrl, formatDate, initials, statusLabel } from '../constants';

const headSx = { bgcolor: '#f8fafc', color: '#64748b', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', py: 1, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };

const SORTS = [
  { value: 'name', label: 'Nom A–Z' },
  { value: 'date_desc', label: 'Plus récent' },
];

const CopyLink = ({ url }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, pl: 1.25, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
      <LinkedInIcon sx={{ color: '#0a66c2', fontSize: 20 }} />
      <Typography noWrap title={url} sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem', color: '#475569' }}>{url}</Typography>
      <Tooltip title={copied ? 'Copié' : 'Copier le lien'}>
        <IconButton
          size="small"
          onClick={async () => {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          }}
          sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#fff' }}
        >
          {copied ? <CheckIcon fontSize="small" sx={{ color: '#16a34a' }} /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Ouvrir sur LinkedIn">
        <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ borderRadius: '8px', bgcolor: '#03407b', color: '#fff', '&:hover': { bgcolor: '#02357a' } }}>
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export const CandidateDrawer = ({ item, onClose, onDelete, crmId, onAddToCrm, onOpenCrm }) => {
  if (!item) return null;
  const p = item.payload || {};
  const url = candidateUrl(item);
  const stored = item.status === 'stored';
  const crmActions = stored && onAddToCrm;
  return (
    <Drawer anchor="right" open={!!item} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 520 }, display: 'flex', flexDirection: 'column' } } }}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #eef2f7', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 46, height: 46, borderRadius: '12px', background: 'linear-gradient(135deg, #0556a4, #03407b)', color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {initials(candidateName(item))}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>{candidateName(item)}</Typography>
          <Typography noWrap sx={{ fontSize: '0.78rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PlaceOutlinedIcon sx={{ fontSize: 15 }} />{candidateLocation(item) || 'Localisation inconnue'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <Box sx={{ p: 1.5, borderRadius: '12px', border: `1px solid ${stored ? '#bbf7d0' : '#fde68a'}`, bgcolor: stored ? '#f0fdf4' : '#fffbeb' }}>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, color: stored ? '#16a34a' : '#b45309' }}>{statusLabel(item)}</Typography>
          {item.reason_detail && <Typography sx={{ fontSize: '0.78rem', color: '#475569', mt: 0.5, lineHeight: 1.5 }}>{item.reason_detail}</Typography>}
        </Box>
        <Box><SectionTitle>En un coup d'œil</SectionTitle><ProfileSummary item={item} /></Box>
        {url && <Box><SectionTitle>Profil LinkedIn</SectionTitle><CopyLink url={url} /></Box>}
        <Box><SectionTitle>Rôles</SectionTitle><Tags values={p.roles_held || (item.primary_role ? [item.primary_role] : [])} color="#334155" bg="#f1f5f9" /></Box>
        <Box><SectionTitle>Compétences techniques</SectionTitle><Tags values={p.technical_skills} /></Box>
        <Box><SectionTitle>Langages de programmation</SectionTitle><Tags values={p.programming_languages} color="#0556a4" bg="#f0f7fd" /></Box>
        {p.summary && (
          <Box>
            <SectionTitle>Résumé</SectionTitle>
            <Typography sx={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{p.summary}</Typography>
          </Box>
        )}
      </Box>

      {(crmActions || (onDelete && stored)) && (
        <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #eef2f7', bgcolor: '#fbfdff', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {crmActions && (crmId ? (
            <AppButton variant="outline" startIcon={<OpenInNewIcon />} onClick={() => onOpenCrm(crmId)} sx={{ flex: 1 }}>
              Voir dans le CRM
            </AppButton>
          ) : (
            <AppButton startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => onAddToCrm(item)} sx={{ flex: 1 }}>
              Ajouter au CRM
            </AppButton>
          ))}
          {onDelete && stored && (
            <Tooltip title="Retirer ce profil du sourcing (les candidats du CRM ne sont pas touchés)">
              <span>
                <AppButton variant="danger" startIcon={<DeleteOutlinedIcon />} onClick={() => onDelete(item)}>Supprimer</AppButton>
              </span>
            </Tooltip>
          )}
        </Box>
      )}
    </Drawer>
  );
};

const CandidateBrowser = ({
  items, loading, selectable = false, selected = [], onToggle, onToggleAll,
  onRetry, busyUrl, retryLabel = 'Relancer ce profil', showStoredAt = true, onDelete, emptyTitle, emptyText,
  crmLinks = {}, onAddToCrm, onOpenCrm,
}) => {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('name');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [opened, setOpened] = useState(null);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('fr');
    const list = items.filter((item) => {
      if (!needle) return true;
      const p = item.payload || {};
      return [p.full_name, item.full_name, ...(p.roles_held || []), item.primary_role, p.location, item.location, ...(p.technical_skills || []), ...(p.programming_languages || [])]
        .filter(Boolean).join(' ').toLocaleLowerCase('fr').includes(needle);
    });
    return [...list].sort((a, b) =>
      sort === 'date_desc'
        ? String(b.stored_at || b.updated_at || '').localeCompare(String(a.stored_at || a.updated_at || ''))
        : candidateName(a).localeCompare(candidateName(b), 'fr'));
  }, [items, query, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * pageSize, current * pageSize);
  const visibleUrls = visible.map(candidateUrl).filter(Boolean);
  const allChecked = visibleUrls.length > 0 && visibleUrls.every((u) => selected.includes(u));

  return (
    <>
      <ListToolbar
        query={query}
        onQuery={(v) => { setQuery(v); setPage(1); }}
        placeholder="Rechercher un nom, un rôle, une compétence ou un lieu…"
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={SORTS}
        count={filtered.length}
      />

      {loading ? (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rounded" height={46} />)}
        </Box>
      ) : filtered.length === 0 ? (
        <EmptyState icon={<PeopleOutlineIcon />} title={query ? 'Aucun résultat' : emptyTitle || 'Aucun candidat'} text={query ? 'Essayez un autre mot-clé.' : emptyText} />
      ) : (
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 760 }}>
            <TableHead>
              <TableRow>
                {selectable && (
                  <TableCell sx={{ ...headSx, width: 44, px: 1 }}>
                    <Checkbox size="small" checked={allChecked} indeterminate={!allChecked && visibleUrls.some((u) => selected.includes(u))} onChange={() => onToggleAll(visibleUrls, !allChecked)} />
                  </TableCell>
                )}
                <TableCell sx={{ ...headSx, width: '30%' }}>Candidat</TableCell>
                <TableCell sx={headSx}>Compétences</TableCell>
                {showStoredAt && <TableCell sx={{ ...headSx, width: 110 }}>Stocké le</TableCell>}
                <TableCell sx={{ ...headSx, width: '26%' }}>Statut</TableCell>
                <TableCell sx={{ ...headSx, width: 56 + (onRetry ? 36 : 0) + (onAddToCrm ? 36 : 0) }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((item, i) => {
                const url = candidateUrl(item);
                const p = item.payload || {};
                const stored = item.status === 'stored';
                return (
                  <TableRow key={item.candidate_uuid || url || i} hover onClick={() => setOpened(item)} sx={{ cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 1 } }}>
                    {selectable && (
                      <TableCell sx={{ px: 1 }} onClick={(e) => e.stopPropagation()}>
                        <Checkbox size="small" checked={selected.includes(url)} onChange={() => onToggle(url)} />
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: '#ebf4fb', color: '#03407b', fontSize: '0.72rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {initials(candidateName(item))}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Typography noWrap sx={{ minWidth: 0, fontSize: '0.86rem', fontWeight: 700, color: '#0f172a' }}>{candidateName(item)}</Typography>
                            <SeniorityChip item={item} />
                          </Box>
                          <Typography noWrap sx={{ fontSize: '0.74rem', color: '#64748b' }}>
                            {[candidateRole(item), candidateLocation(item)].filter(Boolean).join(' · ') || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Tags values={[...(p.technical_skills || []), ...(p.programming_languages || [])]} max={3} nowrap empty="—" /></TableCell>
                    {showStoredAt && <TableCell sx={{ fontSize: '0.78rem', color: '#64748b' }}>{formatDate(item.stored_at)}</TableCell>}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 700, color: stored ? '#16a34a' : '#b45309' }}>{statusLabel(item)}</Typography>
                        {crmLinks[item.candidate_uuid] && (
                          <Chip size="small" label="Dans le CRM" sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: '#ebf4fb', color: '#03407b', flexShrink: 0 }} />
                        )}
                      </Box>
                      {item.reason_detail && <Typography noWrap title={item.reason_detail} sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{item.reason_detail}</Typography>}
                      {item.smart_retry_count > 0 && (
                        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{item.smart_retry_count} reprise(s) intelligente(s)</Typography>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {onRetry && (
                          <Tooltip title={retryLabel}>
                            <span>
                              <IconButton size="small" disabled={!!busyUrl} onClick={() => onRetry(url)} sx={{ color: '#03407b', border: '1px solid #cfe0f1', borderRadius: '8px' }}>
                                <ReplayIcon sx={{ fontSize: 17, animation: busyUrl === url ? 'src-spin 1s linear infinite' : 'none', '@keyframes src-spin': { to: { transform: 'rotate(-360deg)' } } }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {onAddToCrm && stored && (
                          crmLinks[item.candidate_uuid] ? (
                            <Tooltip title="Voir dans le CRM">
                              <IconButton size="small" onClick={() => onOpenCrm(crmLinks[item.candidate_uuid])} sx={{ color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                                <OpenInNewIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Ajouter au CRM">
                              <IconButton size="small" onClick={() => onAddToCrm(item)} sx={{ color: '#03407b', border: '1px solid #cfe0f1', borderRadius: '8px' }}>
                                <PersonAddAlt1OutlinedIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                        <Tooltip title="Voir la fiche">
                          <IconButton size="small" onClick={() => setOpened(item)} sx={{ color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                            <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && filtered.length > 0 && (
        <Pager page={current} pages={pages} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
      )}

      <CandidateDrawer
        item={opened}
        onClose={() => setOpened(null)}
        onDelete={onDelete ? (item) => { setOpened(null); onDelete(item); } : undefined}
        crmId={opened ? crmLinks[opened.candidate_uuid] : null}
        onAddToCrm={onAddToCrm ? (item) => { setOpened(null); onAddToCrm(item); } : undefined}
        onOpenCrm={onOpenCrm}
      />
    </>
  );
};


export default CandidateBrowser;
